import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useMockDate } from './MockDateContext';
import { API_URL } from '../config/api';
import { updateNotificationStage } from '../utils/notifications';

/* ── Stage constants ── */
export const STAGE = {
  PERIOD:     'period_tracking',
  TTC:        'trying_to_conceive',
  PREGNANT:   'pregnant',
  POSTPARTUM: 'postpartum',
  BABY:       'baby_care',
};

const defaultJourney = {
  stage:        STAGE.PERIOD,
  pregnancy:    null,
  baby:         null,
  lastPeriod:   null,
  cycleLength:  28,
  periodLength: 5,
  cycleType:    null,
};

const JourneyContext = createContext();

/* ── localStorage cache helpers (user-scoped) ── */
const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeCache = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify(data)); }
  catch {}
};

/* ── Helpers — accept nowMs so mock date works ── */
const computeWeek = (lmpDate, nowMs) => {
  if (!lmpDate) return 0;
  const weeks = Math.floor((nowMs - new Date(lmpDate)) / (1000 * 60 * 60 * 24 * 7));
  return Math.max(0, Math.min(weeks, 42));
};

const computeDaysUntilDue = (dueDate, nowMs) => {
  if (!dueDate) return null;
  return Math.max(0, Math.ceil((new Date(dueDate) - nowMs) / (1000 * 60 * 60 * 24)));
};

const computeBabyAge = (birthDate, nowMs) => {
  if (!birthDate) return { days: 0, months: 0, weeks: 0 };
  const ms   = nowMs - new Date(birthDate);
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  return { days, weeks: Math.floor(days / 7), months: Math.floor(days / 30.4) };
};

const computeNextPeriod = (lastPeriod, cycleLength, nowMs) => {
  if (!lastPeriod) return null;
  const next = new Date(lastPeriod);
  next.setDate(next.getDate() + cycleLength);
  return { date: next, daysUntil: Math.ceil((next - nowMs) / (1000 * 60 * 60 * 24)) };
};

const computeOvulation = (lastPeriod, cycleLength, nowMs) => {
  if (!lastPeriod) return null;
  const ov = new Date(lastPeriod);
  ov.setDate(ov.getDate() + cycleLength - 14);
  const fertileStart = new Date(ov); fertileStart.setDate(ov.getDate() - 5);
  const fertileEnd   = new Date(ov); fertileEnd.setDate(ov.getDate() + 1);
  return { date: ov, daysUntil: Math.ceil((ov - nowMs) / (1000 * 60 * 60 * 24)), fertileStart, fertileEnd };
};

/* ── Provider ── */
export const JourneyProvider = ({ children, userId }) => {
  const { nowMs: getNowMs } = useMockDate();

  /* User-scoped localStorage key — prevents cross-user data leakage on shared devices */
  const cacheKey = userId ? `ts_journey_${userId}` : null;

  /* Initialize from localStorage immediately — no flash of empty state on reload */
  const [journey, setJourney] = useState(() => {
    if (!cacheKey) return defaultJourney;
    const c = readCache(cacheKey);
    if (!c) return defaultJourney;
    return {
      stage:        c.stage        || defaultJourney.stage,
      pregnancy:    c.pregnancy    || null,
      baby:         c.baby         || null,
      lastPeriod:   c.lastPeriod   || null,
      cycleLength:  c.cycleLength  ?? defaultJourney.cycleLength,
      periodLength: c.periodLength ?? defaultJourney.periodLength,
      cycleType:    c.cycleType    || null,
    };
  });
  const [journeyLoading, setJourneyLoading] = useState(true);

  /* Fetch journey from MongoDB on mount */
  useEffect(() => {
    const token = localStorage.getItem('tinysteps_token');
    if (!token) { setJourneyLoading(false); return; }

    axios.get(`${API_URL}/journey`)
      .then(res => {
        const data = res.data;
        const fromAPI = {
          stage:        data.stage        || defaultJourney.stage,
          pregnancy:    data.pregnancy    || null,
          baby:         data.baby         || null,
          lastPeriod:   data.lastPeriod   || null,
          cycleLength:  data.cycleLength  ?? defaultJourney.cycleLength,
          periodLength: data.periodLength ?? defaultJourney.periodLength,
          cycleType:    data.cycleType    || null,
        };

        /* Recovery: if API has null lastPeriod but local cache has it, the backend
           save failed in a previous session. Restore from cache and re-sync to API. */
        const cached = cacheKey ? readCache(cacheKey) : null;
        if (!fromAPI.lastPeriod && cached?.lastPeriod) {
          const restored = {
            ...fromAPI,
            lastPeriod:   cached.lastPeriod,
            cycleLength:  cached.cycleLength  ?? fromAPI.cycleLength,
            periodLength: cached.periodLength ?? fromAPI.periodLength,
            cycleType:    cached.cycleType    || fromAPI.cycleType || null,
          };
          setJourney(restored);
          if (cacheKey) writeCache(cacheKey, restored);
          /* Re-sync recovered data to the backend silently */
          axios.put(`${API_URL}/journey`, {
            stage: restored.stage, pregnancy: restored.pregnancy, baby: restored.baby,
            lastPeriod: restored.lastPeriod, cycleLength: restored.cycleLength, periodLength: restored.periodLength,
            cycleType: restored.cycleType,
          }).catch(() => {});
        } else {
          setJourney(fromAPI);
          if (cacheKey) writeCache(cacheKey, fromAPI);
        }
      })
      .catch(() => { /* keep cached/defaults silently — API unreachable */ })
      .finally(() => setJourneyLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Persist journey state to MongoDB AND localStorage */
  const save = (data) => {
    const next = { ...defaultJourney, ...data };
    const stageChanged = next.stage !== journey.stage;
    setJourney(next);
    /* Write to localStorage immediately so data survives if API call fails */
    if (cacheKey) writeCache(cacheKey, next);
    if (stageChanged) updateNotificationStage(next.stage);
    axios.put(`${API_URL}/journey`, {
      stage:        next.stage,
      pregnancy:    next.pregnancy,
      baby:         next.baby,
      lastPeriod:   next.lastPeriod,
      cycleLength:  next.cycleLength,
      periodLength: next.periodLength,
      cycleType:    next.cycleType,
    }).catch(err => console.warn('[Journey] save failed:', err.message));
  };

  /* ── Derived computed values ── */
  const stage        = journey.stage;
  const isPregnant   = stage === STAGE.PREGNANT;
  const isPostpartum = stage === STAGE.POSTPARTUM;
  const isBabyCare   = stage === STAGE.BABY;
  const isBabyMode   = isPostpartum || isBabyCare;
  const isPrePreg    = stage === STAGE.PERIOD || stage === STAGE.TTC;
  const isTTC        = stage === STAGE.TTC;

  const _nowMs = getNowMs();

  const currentWeek  = isPregnant ? computeWeek(journey.pregnancy?.lmpDate, _nowMs) : 0;
  const daysUntilDue = isPregnant ? computeDaysUntilDue(journey.pregnancy?.dueDate, _nowMs) : null;
  const trimester    = currentWeek <= 13 ? 1 : currentWeek <= 27 ? 2 : 3;
  const babyAge      = isBabyMode ? computeBabyAge(journey.baby?.birthDate, _nowMs) : null;
  const nextPeriod   = isPrePreg ? computeNextPeriod(journey.lastPeriod, journey.cycleLength, _nowMs) : null;
  const ovulation    = isPrePreg ? computeOvulation(journey.lastPeriod, journey.cycleLength, _nowMs) : null;

  const cycleDay = (() => {
    if (!isPrePreg || !journey.lastPeriod) return null;
    const days = Math.floor((_nowMs - new Date(journey.lastPeriod)) / (1000 * 60 * 60 * 24));
    // Never use modulo — that wraps to Day 1 on the predicted period date before the
    // user has actually started a new period, creating a phantom "Day 1" cycle entry.
    // Cap at cycleLength so the counter stops advancing once the predicted date passes.
    return Math.min(days + 1, journey.cycleLength);
  })();

  /* ── Lifecycle transition methods ── */
  const confirmPregnancy = (lmpDate) => {
    const lmp = new Date(lmpDate);
    const due = new Date(lmp);
    due.setDate(due.getDate() + 280);
    save({
      ...journey,
      stage:     STAGE.PREGNANT,
      pregnancy: { lmpDate, dueDate: due.toISOString().split('T')[0], confirmedAt: new Date().toISOString() },
    });
  };

  const confirmDelivery = (babyData) => {
    save({
      ...journey,
      stage: STAGE.BABY,
      baby:  { ...babyData, birthDate: babyData.birthDate || new Date().toISOString().split('T')[0] },
      pregnancy: journey.pregnancy, // keep for memory
    });
  };

  const setStage = (newStage) => {
    save({ ...journey, stage: newStage });
  };

  const logLastPeriod = (date) => {
    save({ ...journey, lastPeriod: date });
  };

  const updateCycleSettings = (cycleLength, periodLength) => {
    save({ ...journey, cycleLength, periodLength });
  };

  const resetToMenstrual = () => {
    /* Clear local cache when intentionally resetting the journey */
    if (cacheKey) localStorage.removeItem(cacheKey);
    save({ ...defaultJourney });
  };

  return (
    <JourneyContext.Provider value={{
      journey, stage, save, journeyLoading,
      /* state flags */
      isPregnant, isPostpartum, isBabyCare, isBabyMode, isPrePreg, isTTC,
      /* derived values */
      currentWeek, daysUntilDue, trimester,
      babyAge, nextPeriod, ovulation, cycleDay,
      /* transition methods */
      confirmPregnancy, confirmDelivery, setStage, logLastPeriod, updateCycleSettings, resetToMenstrual,
    }}>
      {children}
    </JourneyContext.Provider>
  );
};

export const useJourney = () => {
  const ctx = useContext(JourneyContext);
  if (!ctx) throw new Error('useJourney must be used within JourneyProvider');
  return ctx;
};

export default JourneyContext;
